import React, { useState } from 'react';
import { App, Button, Input, Space } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { canManageProductCatalog } from '../../modules/products/productCatalogAccessPolicy';
import type { ProductSelectionPurchasePriceScope } from '../../modules/products/productSelectionApi';
import { useProductSelectionCatalog } from '../../modules/products/useProductSelectionCatalog';
import { authService } from '../../services/auth';
import type { Product, ProductVariant } from '../../types/pz';
import AddProductModal from './AddProductModal';
import ProductPickerModal from './ProductPickerModal';
import './ProductNameCombobox.css';

interface ProductNameComboboxProps {
  value?: string;
  onChange: (productName: string) => void;
  onSelectVariant: (variantId: string | undefined, variant?: ProductVariant) => void;
  placeholder?: string;
  disabled?: boolean;
  'data-testid'?: string;
  allowCreate?: boolean;
  showInventory?: boolean;
  preferredVariantIds?: readonly string[];
  selectedVariantId?: string | null;
  purchasePriceScope?: ProductSelectionPurchasePriceScope;
}

const ProductNameCombobox: React.FC<ProductNameComboboxProps> = ({
  value,
  onChange,
  onSelectVariant,
  placeholder,
  disabled = false,
  'data-testid': dataTestId,
  allowCreate = false,
  showInventory = true,
  preferredVariantIds = [],
  selectedVariantId,
  purchasePriceScope,
}) => {
  const { t } = useTranslation(['products']);
  const { message } = App.useApp();
  const { variants, loading, reload } = useProductSelectionCatalog(true, purchasePriceScope);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const currentUser = authService.getCurrentUser();
  const canCreate = allowCreate && canManageProductCatalog(currentUser);

  const handleProductCreated = async (product: Product) => {
    setCreateOpen(false);
    const refreshed = await reload();
    const created = refreshed.filter((variant) => variant.productId === product.id);
    const defaultVariant = created.find((variant) => variant.isDefault)
      || created.find((variant) => variant.variantName === 'Standard')
      || created[0];
    if (!defaultVariant) {
      message.warning(t('products:addProductModal.variantNotFound'));
      return;
    }
    onSelectVariant(defaultVariant.id, defaultVariant);
  };

  return (
    <>
      <Space.Compact className="product-name-combobox">
        <Input
          value={value}
          onChange={(event) => onChange(event.target.value)}
          disabled={disabled}
          placeholder={placeholder}
          data-testid={dataTestId}
        />
        <Button
          icon={<SearchOutlined />}
          disabled={disabled}
          loading={loading && variants.length === 0}
          onClick={() => setPickerOpen(true)}
          aria-label={t('products:catalogPicker.openButton')}
        >
          {t('products:catalogPicker.chooseButton')}
        </Button>
      </Space.Compact>

      <ProductPickerModal
        open={pickerOpen}
        variants={variants}
        loading={loading}
        selectedVariantId={selectedVariantId}
        preferredVariantIds={preferredVariantIds}
        showInventory={showInventory}
        canCreate={canCreate}
        onRefresh={reload}
        onRequestCreate={() => {
          setPickerOpen(false);
          setCreateOpen(true);
        }}
        onSelect={(variant) => {
          onSelectVariant(variant.id, variant);
          setPickerOpen(false);
        }}
        onCancel={() => setPickerOpen(false)}
      />

      {canCreate && (
        <AddProductModal
          open={createOpen}
          onCancel={() => setCreateOpen(false)}
          onCreated={handleProductCreated}
        />
      )}
    </>
  );
};

export default ProductNameCombobox;
